import { useEffect, useState, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { Download, LogOut, Menu, Moon, Radio, Sparkles, Tag, X } from 'lucide-react';
import { ThanhBen, type ManHinh } from './components/ThanhBen';
import { ManTongQuan } from './screens/TongQuan';
import { CustomerSearchScreen } from './screens/CustomerSearch';
import { ManTaoBai } from './screens/TaoBai';
import { ManTienTrinh } from './screens/TienTrinh';
import { ManLichDinhKy } from './screens/LichDinhKy';
import { ManBinhLuan } from './screens/BinhLuan';
import { ManMcpAgent } from './screens/McpAgent';
import { ManKenhNhom } from './screens/KenhNhom';
import { HelpCenter, HelpCenterErrorBoundary } from './screens/HelpCenter';
import { api, LoiMayChu } from './services/api';
import type { Phien, TrangThaiExtension, QuyenLoi, Goi, DonHang } from './services/kieu';
import { DangTai, ChuaCoDuLieu } from './components/ui';
import { avatarActor, chonTaiKhoan, khoaLuaChon, moTaActor, tenActor, tenVietTatActor } from './services/taiKhoan';
import { useKhoaGui } from './services/khoaGui';
import { rehydrateSessionToken, clearSessionCredentials } from './services/sessionRestore';

const NHAN_MAN: Record<ManHinh, string> = {
  'tong-quan': 'Tổng quan',
  'tao-bai': 'Tạo bài',
  'lich-dinh-ky': 'Lịch định kỳ',
  'kho-bai-viet': 'Kho bài viết',
  'tien-trinh': 'Tiến Trình',
  'kenh-nhom': 'Kênh & nhóm',
  'binh-luan': 'Bình luận',
  'tim-kiem-kh': 'Tìm kiếm khách hàng',
  'mcp-agent': 'MCP Agent',
  'tro-giup': 'Trung tâm trợ giúp',
};

const EXTENSION_VERSION_TOI_THIEU = '0.8.2';

function phienBanThapHon(hienTai: string | null, toiThieu: string): boolean {
  if (!hienTai) return true;
  const parse = (value: string) => value.split('.').map(part => Number(part));
  const current = parse(hienTai);
  const minimum = parse(toiThieu);
  if ([...current, ...minimum].some(part => !Number.isInteger(part) || part < 0)) return true;
  for (let index = 0; index < Math.max(current.length, minimum.length); index += 1) {
    const left = current[index] || 0;
    const right = minimum[index] || 0;
    if (left !== right) return left < right;
  }
  return false;
}

/**
 * Khoá idempotency cho lượt mua gói, sinh MỘT LẦN mỗi lần tải trang.
 *
 * Vì sao không sinh mới mỗi lần bấm: khoá mới tức là đơn mới. Người dùng bấm
 * "Chọn gói" hai lần vì mạng chậm sẽ nhận hai mã chuyển khoản khác nhau và
 * không biết dùng mã nào — trong khi tiền thì chỉ chuyển một lần.
 *
 * Giữ cố định theo lượt tải trang thì máy chủ nhận ra và trả lại đúng đơn cũ
 * (server.js:834). Muốn tạo đơn khác thật sự thì tải lại trang.
 */
const khoaPhienMua = (() => {
  try {
    return crypto.randomUUID();
  } catch {
    // Trình duyệt cũ không có randomUUID. Vẫn phải có khoá, thà kém ngẫu nhiên
    // còn hơn không có — thiếu khoá là mở đường cho đơn trùng.
    return 'p' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }
})();

export default function App() {
  const [man, setMan] = useState<ManHinh>(() => {
    try {
      if (typeof window !== 'undefined') {
        const hash = window.location.hash.replace(/^#/, '');
        if (hash === 'tro-giup' || hash === 'help') return 'tro-giup';
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('tab');
        if (tab === 'tro-giup' || tab === 'help') return 'tro-giup';
      }
    } catch {}
    return 'tong-quan';
  });
  const [moMenu, setMoMenu] = useState(false);
  const [toi, setToi] = useState(() => localStorage.getItem('giaoDien') === 'toi');

  const [phien, setPhien] = useState<Phien | null>(null);
  const [ext, setExt] = useState<TrangThaiExtension | null>(null);
  const [uidDaChon, setUidDaChon] = useState<string | null>(null);
  const [dongBoLuc, setDongBoLuc] = useState<string>('');
  const [loi, setLoi] = useState<string | null>(null);
  const [moNangCap, setMoNangCap] = useState(false);

  // Guards against stale async reload/logout race conditions
  const sessionSeqRef = useRef(0);
  const isLoggingOutRef = useRef(false);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', toi);
    localStorage.setItem('giaoDien', toi ? 'toi' : 'sang');
  }, [toi]);

  const lamMoi = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    const seq = ++sessionSeqRef.current;
    const requestTimestamp = Date.now();
    try {
      const [p, e] = await Promise.allSettled([api.phien(), api.trangThaiExtension()]);
      if (isLoggingOutRef.current || seq !== sessionSeqRef.current) return;
      if (p.status === 'fulfilled') {
        setPhien(p.value);
        rehydrateSessionToken(p.value, { requestTimestamp });
      } else if (p.status === 'rejected') {
        const err = p.reason;
        if (err instanceof LoiMayChu && err.trangThai === 401) {
          clearSessionCredentials();
        }
      }
      if (e.status === 'fulfilled') setExt(e.value);
      else setExt(null);
      setDongBoLuc(new Date().toLocaleTimeString('vi-VN'));
      setLoi(null);
    } catch (err) {
      if (isLoggingOutRef.current || seq !== sessionSeqRef.current) return;
      setLoi(err instanceof LoiMayChu ? err.message : 'Không tải được dữ liệu.');
    }
  }, []);

  useEffect(() => {
    lamMoi();
    // Máy chủ có thể khởi động lại (deploy). Hỏi lại định kỳ để giao diện
    // không kẹt ở trạng thái cũ — đây là bài học từ BUG-5.
    const t = setInterval(lamMoi, 20000);
    return () => clearInterval(t);
  }, [lamMoi]);

  /**
   * Đăng xuất.
   *
   * Lỗi KHÔNG còn bị nuốt im lặng như trước (`catch(() => {})`). Nếu máy chủ
   * không nhận được lệnh đăng xuất, người dùng phải biết — nếu không họ rời máy
   * mà tưởng phiên đã đóng, trong khi cookie vẫn còn hiệu lực.
   *
   * Vẫn chuyển trang khi lỗi mạng, vì giữ người dùng lại màn hình cũ cũng không
   * an toàn hơn. Nhưng báo cho họ biết trước đã.
   */
  const dangXuat = useCallback(async () => {
    isLoggingOutRef.current = true;
    sessionSeqRef.current++;
    clearSessionCredentials();
    setPhien(null);
    try {
      await api.dangXuat();
    } catch (err) {
      const moTa = err instanceof LoiMayChu ? err.message : 'Không liên lạc được máy chủ.';
      setLoi('Chưa đóng được phiên trên máy chủ: ' + moTa + ' Nếu đang dùng máy chung, hãy xoá cookie trình duyệt.');
      await new Promise(r => setTimeout(r, 2500));
    }
    location.href = '/login';
  }, []);

  const daDangNhap = Boolean(phien?.user);
  useEffect(() => {
    if (!daDangNhap) return;
    const socket = io('/', { transports: ['websocket', 'polling'], withCredentials: true });
    // Drop readiness immediately on any connection/actor transition, then
    // reconcile the authenticated snapshot; never apply an event as authority.
    let revision = 0;
    let disposed = false;
    const reconcile = (event?: { uid?: string; connected?: boolean; identityState?: string }) => {
      const current = ++revision;
      setExt(previous => event?.connected === false || event?.identityState === 'UNAVAILABLE'
        || (event?.uid && previous?.uid && event.uid !== previous.uid) ? null : previous);
      void api.trangThaiExtension().then(snapshot => {
        if (!disposed && current === revision) setExt(snapshot);
      }).catch(() => { if (!disposed && current === revision) setExt(null); });
    };
    socket.on('connect', reconcile);
    socket.on('EXTENSION_STATUS_UPDATE', reconcile);
    socket.on('disconnect', () => { revision++; setExt(null); });
    return () => { disposed = true; revision++; socket.disconnect(); };
  }, [daDangNhap, lamMoi]);
  const taiKhoan = chonTaiKhoan(ext, uidDaChon);
  const noiDuoc = Boolean(taiKhoan);
  const [failedActorAvatar, setFailedActorAvatar] = useState<string | null>(null);
  const actorAvatarKey = `${taiKhoan?.uid}:${avatarActor(taiKhoan)}`;
  const phienBanExtension = taiKhoan?.version || ext?.accounts?.[0]?.version || null;
  const buildExtension = taiKhoan?.build || ext?.accounts?.[0]?.build || null;
  const extensionCanCapNhat = noiDuoc && phienBanThapHon(phienBanExtension, EXTENSION_VERSION_TOI_THIEU);
  useEffect(() => {
    if (!phien?.user?.tenantId) return;
    const khoa = khoaLuaChon(phien.user.tenantId);
    const previousUid = localStorage.getItem(khoa);
    const uid = chonTaiKhoan(ext, previousUid)?.uid || previousUid || null;
    setUidDaChon(uid);
    if (uid) localStorage.setItem(khoa, uid); else localStorage.removeItem(khoa);
  }, [ext, phien?.user?.tenantId]);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <ThanhBen
        dangMo={man}
        doiMan={setMan}
        moTrenDienThoai={moMenu}
        dongMenu={() => setMoMenu(false)}
        daDangNhap={daDangNhap}
        onDangXuat={dangXuat}
      />

      {moMenu && (
        <div className="fixed inset-0 z-30 bg-slate-900/40 md:hidden" onClick={() => setMoMenu(false)} />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-800 sm:px-6">
          <button onClick={() => setMoMenu(true)} aria-label="Mở menu" className="rounded-lg p-2 text-slate-500 md:hidden">
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="min-w-0 flex-1 truncate text-lg font-bold text-slate-900 dark:text-white sm:text-lg">
            {NHAN_MAN[man]}
          </h1>

          <div className="hidden min-w-0 items-center gap-2 rounded-full bg-slate-50 px-2.5 py-1.5 dark:bg-slate-700/50 lg:flex" title={taiKhoan ? tenActor(taiKhoan) : 'Chưa có actor Facebook'}>
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Actor</span>
            <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-[10px] font-bold text-slate-600 dark:bg-slate-600 dark:text-slate-200">
              {avatarActor(taiKhoan) && failedActorAvatar !== actorAvatarKey ? <img src={avatarActor(taiKhoan) || ''} alt="Ảnh actor Facebook" onError={() => setFailedActorAvatar(actorAvatarKey)} className="h-full w-full object-cover" /> : <span title="Chưa tải được ảnh actor Facebook">{tenVietTatActor(taiKhoan)}</span>}
            </span>
            <span className="max-w-[190px] truncate text-xs font-semibold text-slate-700 dark:text-slate-200">{moTaActor(taiKhoan)}</span>
          </div>

          <span
            className={
              'hidden items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold sm:inline-flex ' +
              (extensionCanCapNhat
                ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400'
                : noiDuoc
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'
                : 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400')
            }
            title={buildExtension ? `Extension ${phienBanExtension || '?'} · ${buildExtension}` : 'Chưa xác định được build Extension'}
          >
            <Radio className="h-3.5 w-3.5" />
            {noiDuoc ? `${taiKhoan?.identityState === 'VERIFIED' ? 'Sẵn sàng · Đã nhận diện actor' : 'Đã kết nối · Chưa xác minh nhãn actor'}${phienBanExtension ? ` · Ext ${phienBanExtension}` : ''}` : 'Chưa sẵn sàng · Kiểm tra kết nối'}
            {extensionCanCapNhat ? ' · cần cập nhật' : ''}
          </span>

          <a
            href="/api/downloads/extension"
            className="hidden items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-brand-500/40 dark:hover:bg-brand-500/10 dark:hover:text-brand-300 sm:inline-flex"
            title="Tải bản FBMax Companion Extension mới nhất"
          >
            <Download className="h-3.5 w-3.5" />
            {noiDuoc ? 'Cập nhật Extension' : 'Tải Extension'}
          </a>

          {phien?.entitlement?.active ? (
            /* Thẻ gói phải BẤM ĐƯỢC. Trước đây là <span> trơ: người đang dùng gói
               bấm vào không ra gì — cùng lỗi câm với khối tài khoản góc phải.
               Đây cũng là chỗ tự nhiên nhất để họ xem còn bao lâu và gia hạn sớm. */
            <button
              onClick={() => setMoNangCap(true)}
              title="Xem bảng giá và hạn dùng"
              className="hidden rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 sm:inline-flex dark:bg-emerald-500/10 dark:text-emerald-400 dark:hover:bg-emerald-500/20"
            >
              {phien.entitlement.planName} · còn {phien.entitlement.daysLeft} ngày
            </button>
          ) : (
            <button onClick={() => setMoNangCap(true)} className="hidden items-center gap-1.5 rounded-full bg-amber-400 px-3 py-1.5 text-xs font-bold text-amber-950 sm:inline-flex">
              <Sparkles className="h-3.5 w-3.5" />
              Gia hạn gói
            </button>
          )}

          <button onClick={() => setToi(v => !v)} aria-label="Đổi giao diện" className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700">
            <Moon className="h-[18px] w-[18px]" />
          </button>

          <MenuTaiKhoan
            phien={phien}
            onDangXuat={dangXuat}
            onNangCap={() => setMoNangCap(true)}
          />
        </header>

        {moNangCap && (
          <PopupBangGia
            quyen={phien?.entitlement || null}
            onDong={() => setMoNangCap(false)}
            onDaMua={lamMoi}
          />
        )}

        <main key={`${phien?.user?.tenantId || ''}:${uidDaChon || ''}:${man === 'tim-kiem-kh' ? '' : taiKhoan?.identityContext?.epoch || ''}`} className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
          {(ext?.accounts.length || 0) > 1 && <select aria-label="Tài khoản Facebook" value={taiKhoan?.uid || ''} onChange={e => {
            if (!phien?.user) return;
            localStorage.setItem(khoaLuaChon(phien.user.tenantId), e.target.value);
            setUidDaChon(e.target.value);
          }}>
            <option value="" disabled>Chọn tài khoản</option>
            {ext?.accounts.map(t => <option key={t.uid} value={t.uid}>{t.name || t.uid}</option>)}
          </select>}
          {dongBoLuc && (
            <p className="mb-3 text-right text-xs text-slate-400">Đồng bộ lần cuối: {dongBoLuc}</p>
          )}
          {loi && (
            <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-400">
              {loi}
            </div>
          )}
          {man === 'tong-quan' ? (
            <ManTongQuan ext={ext} taiKhoan={taiKhoan} entitlementActive={phien?.entitlement?.active} onLamMoi={lamMoi} onTaoBai={() => setMan('tao-bai')} />
          ) : man === 'tao-bai' ? (
            <ManTaoBai
              onXong={lamMoi}
              tenantId={phien?.user?.tenantId}
              fbUid={taiKhoan?.uid}
              dangDungPage={Boolean(taiKhoan?.isPage)}
              onYeuCauNangCap={() => setMoNangCap(true)}
            />
          ) : man === 'tien-trinh' ? (
            <ManTienTrinh onLamMoi={lamMoi} tenantId={phien?.user?.tenantId} fbUid={taiKhoan?.uid} />
          ) : man === 'lich-dinh-ky' ? (
            <ManLichDinhKy ext={ext} onLamMoi={lamMoi} />
          ) : man === 'binh-luan' ? (
            <ManBinhLuan onLamMoi={lamMoi} taiKhoan={taiKhoan} token={phien?.token || null} />
          ) : man === 'mcp-agent' ? (
            <ManMcpAgent ext={ext} />
          ) : man === 'kenh-nhom' ? (
            <ManKenhNhom taiKhoan={taiKhoan} />
          ) : man === 'tim-kiem-kh' ? (
            <CustomerSearchScreen taiKhoan={taiKhoan} />
          ) : man === 'tro-giup' ? (
            <HelpCenterErrorBoundary>
              <HelpCenter
                onDieuHuong={(m) => setMan(m)}
                onMoNangCap={() => setMoNangCap(true)}
                extConnected={noiDuoc}
                extVersion={phienBanExtension}
                hasAccount={Boolean(taiKhoan?.uid)}
              />
            </HelpCenterErrorBoundary>
          ) : (
            <ChuaDung nhan={NHAN_MAN[man]} />
          )}
        </main>
      </div>
    </div>
  );
}

/**
 * ===== POPUP BẢNG GIÁ =====
 *
 * VÌ SAO LÀ POPUP TRONG APP, KHÔNG PHẢI LINK RA TRANG TIẾP THỊ
 *
 * Trước 21/09/2026, "Xem bảng giá" dẫn ra `/dich-vu`. Hai vấn đề:
 *
 *  1. Rời khỏi app giữa chừng. Khách đang làm dở một việc, bấm xem giá, và bị
 *     đẩy sang trang tiếp thị — mất ngữ cảnh, phải tự tìm đường quay lại.
 *
 *  2. Trang đó KHÔNG biết khách đang dùng gói nào. Nó hiển thị y hệt cho người
 *     chưa mua lẫn người đang dùng, nên người đã mua không biết mình đang ở đâu.
 *
 * Popup này đọc `entitlement` của chính phiên đang đăng nhập nên nói được:
 * gói nào đang dùng, còn bao nhiêu ngày, và gói nào mua được tiếp.
 *
 * ===== NGUỒN SỰ THẬT LÀ MÁY CHỦ =====
 *
 * Danh sách gói lấy từ `/api/payments/plans`, KHÔNG viết cứng trong giao diện.
 * Trang `/dich-vu` hiện đang quảng cáo ba gói 199k/499k/999k không tồn tại trong
 * `paymentStore.PLANS` — đó chính là hậu quả của việc chép giá vào HTML tĩnh.
 * Ở đây giá đổi trong `paymentStore.js` thì giao diện đổi theo ngay.
 *
 * ===== SAU KHI TẠO ĐƠN =====
 *
 * Máy chủ trả mã đơn + nội dung chuyển khoản + ảnh QR VietQR dựng sẵn. Khách
 * chuyển tiền, webhook ngân hàng khớp `orderCode` rồi tự kích hoạt
 * (paymentStore.js:294-306). Giao diện KHÔNG tự nhận là đã thanh toán — nó chỉ
 * hiện hướng dẫn và mời bấm "Tôi đã chuyển khoản" để hỏi lại máy chủ.
 */
function PopupBangGia({
  quyen, onDong, onDaMua,
}: { quyen: QuyenLoi | null; onDong: () => void; onDaMua: () => void }) {
  const [danhSach, setDanhSach] = useState<Goi[] | null>(null);
  const [loiTai, setLoiTai] = useState<string | null>(null);
  const [don, setDon] = useState<DonHang | null>(null);
  const [loiMua, setLoiMua] = useState<string | null>(null);
  const [daBaoChuyen, setDaBaoChuyen] = useState(false);
  const [dangMua, chayMua] = useKhoaGui();
  const [dangKiemTra, chayKiemTra] = useKhoaGui();

  useEffect(() => {
    api.goi_danhSach()
      .then(kq => setDanhSach(kq.plans || []))
      .catch(e => setLoiTai(e instanceof LoiMayChu ? e.message : 'Không tải được bảng giá.'));

    api.goi_donCuaToi()
      .then(kq => {
        if (kq.ok && Array.isArray(kq.orders)) {
          const pending = kq.orders.find(o => o.status === 'PENDING');
          if (pending) setDon(pending);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const phim = (e: KeyboardEvent) => { if (e.key === 'Escape') onDong(); };
    document.addEventListener('keydown', phim);
    return () => document.removeEventListener('keydown', phim);
  }, [onDong]);

  async function mua(g: Goi) {
    setLoiMua(null);
    try {
      // Khoá idempotency gắn với GÓI và PHIÊN POPUP này. Bấm lại nhiều lần chỉ
      // ra đúng một đơn; máy chủ trả lại đơn cũ thay vì tạo thêm (server.js:834).
      const khoa = `mua_${g.id}_${khoaPhienMua}`;
      const kq = await api.goi_taoDon(g.id, khoa);
      setDon(kq.order);
      // Gói giá 0 được kích hoạt ngay (paymentStore.js:119) — làm mới để
      // giao diện thấy quyền mới, không bắt khách tự tải lại trang.
      if (kq.order.status === 'ACTIVATED') onDaMua();
    } catch (e) {
      setLoiMua(e instanceof LoiMayChu ? e.message : 'Không tạo được đơn. Thử lại sau ít phút.');
    }
  }

  const tien = (n: number | null | undefined) =>
    n === null || n === undefined ? null : n.toLocaleString('vi-VN') + 'đ';

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 py-10"
      onClick={onDong}
      role="dialog"
      aria-modal="true"
      aria-label="Bảng giá"
    >
      <div
        className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-700 dark:bg-slate-800"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-lg font-bold text-slate-900 dark:text-white">Bảng giá</p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {!quyen
                ? 'Đang đọc trạng thái gói…'
                : quyen.active
                ? `Bạn đang dùng ${quyen.planName || 'gói hiện tại'} · còn ${quyen.daysLeft ?? '?'} ngày.`
                : quyen.status === 'EXPIRED'
                ? `${quyen.planName || 'Gói trước'} đã hết hạn. Gia hạn để mở khoá thao tác.`
                : 'Bạn chưa có gói. Chọn một gói để bắt đầu.'}
            </p>
          </div>
          <button onClick={onDong} className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Đóng">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ===== Đã tạo đơn: hiện hướng dẫn chuyển khoản ===== */}
        {don ? (
          <div className="mt-5">
            <div className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
              <p className="text-sm font-bold text-slate-900 dark:text-white">
                {don.planName} · {tien(don.amount) || 'Liên hệ'}
              </p>

              {don.status === 'ACTIVATED' ? (
                <p className="mt-2 rounded-lg bg-emerald-50 p-3 text-sm font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">
                  Gói đã được kích hoạt.
                </p>
              ) : (
                <>
                  <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                    Chuyển khoản theo thông tin dưới đây. Sau khi nhận được chuyển khoản,
                    quản trị viên sẽ duyệt và kích hoạt gói cho tài khoản của bạn.
                  </p>

                  <div className="mt-3 grid gap-3 sm:grid-cols-[auto_minmax(0,1fr)]">
                    {don.qrUrl && (
                      <img
                        src={don.qrUrl}
                        alt={`Mã QR chuyển khoản ${tien(don.amount)}`}
                        className="h-44 w-44 shrink-0 rounded-lg border border-slate-200 bg-white object-contain dark:border-slate-700"
                      />
                    )}
                    <dl className="space-y-1.5 text-sm">
                      <div><dt className="inline text-slate-500 dark:text-slate-400">Ngân hàng: </dt>
                        <dd className="inline font-semibold text-slate-900 dark:text-white">{don.bank?.bankId}</dd></div>
                      <div><dt className="inline text-slate-500 dark:text-slate-400">Số tài khoản: </dt>
                        <dd className="inline font-mono font-semibold text-slate-900 dark:text-white">{don.bank?.accountNo}</dd></div>
                      <div><dt className="inline text-slate-500 dark:text-slate-400">Chủ tài khoản: </dt>
                        <dd className="inline font-semibold text-slate-900 dark:text-white">{don.bank?.accountName}</dd></div>
                      <div><dt className="inline text-slate-500 dark:text-slate-400">Số tiền: </dt>
                        <dd className="inline font-semibold text-slate-900 dark:text-white">{tien(don.amount)}</dd></div>
                      <div className="pt-1">
                        <dt className="text-slate-500 dark:text-slate-400">Nội dung chuyển khoản:</dt>
                        {/* Phần quan trọng nhất: sai nội dung thì quản trị viên không
                            khớp được đơn, tiền vào mà gói không mở. */}
                        <dd className="mt-0.5 select-all rounded-lg bg-amber-50 px-2.5 py-1.5 font-mono text-sm font-bold text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                          {don.transferContent}
                        </dd>
                      </div>
                    </dl>
                  </div>

                  <p className="mt-3 text-xs text-slate-400">
                    Giữ nguyên nội dung chuyển khoản để hệ thống và quản trị viên đối soát đúng đơn của bạn.
                  </p>
                </>
              )}
            </div>

            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                onClick={() => { setDon(null); setLoiMua(null); setDaBaoChuyen(false); }}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700/50"
              >
                Chọn gói khác
              </button>
              {don.status !== 'ACTIVATED' && (
                <button
                  onClick={() => chayKiemTra(async () => {
                    await onDaMua();
                    setDaBaoChuyen(true);
                  })}
                  disabled={dangKiemTra}
                  aria-busy={dangKiemTra || undefined}
                  className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
                >
                  {dangKiemTra ? 'Đang kiểm tra...' : daBaoChuyen ? 'Đang chờ quản trị viên kích hoạt' : 'Tôi đã chuyển khoản'}
                </button>
              )}
            </div>
            {daBaoChuyen && (
              <p className="mt-2 text-right text-xs font-medium text-amber-600 dark:text-amber-400">
                Chưa ghi nhận kích hoạt. Quản trị viên sẽ kiểm tra sao kê chuyển khoản và kích hoạt gói cho tài khoản của bạn.
              </p>
            )}
          </div>
        ) : (
          /* ===== Chưa tạo đơn: danh sách gói ===== */
          <div className="mt-5">
            {loiTai && (
              <p className="rounded-xl bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-400">{loiTai}</p>
            )}
            {!danhSach && !loiTai && <DangTai dong={2} />}

            {danhSach && danhSach.length === 0 && (
              <ChuaCoDuLieu chu="Chưa có gói nào để mua." goiY="Liên hệ quản trị viên để được mở gói." />
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              {(danhSach || []).map(g => {
                const dangDung = Boolean(quyen?.active && quyen.planId === g.id);
                return (
                  <div
                    key={g.id}
                    className={
                      'rounded-xl border p-4 ' +
                      (dangDung
                        ? 'border-emerald-300 bg-emerald-50/50 dark:border-emerald-500/40 dark:bg-emerald-500/5'
                        : 'border-slate-200 dark:border-slate-700')
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-bold text-slate-900 dark:text-white">{g.name}</p>
                      {dangDung && (
                        <span className="shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400">
                          ĐANG DÙNG
                        </span>
                      )}
                    </div>

                    <p className="mt-1.5 text-2xl font-bold text-slate-900 dark:text-white">
                      {/* `amount: null` nghĩa là CHƯA CHỐT GIÁ. Hiện "0đ" ở đây
                          là hứa miễn phí một thứ chưa có điều khoản. */}
                      {tien(g.amount) ?? 'Sắp có'}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{g.period}</p>

                    <ul className="mt-3 space-y-1 text-xs text-slate-600 dark:text-slate-300">
                      <li>Tài khoản Facebook: {g.limits?.maxFbAccounts ?? 'Không giới hạn'}</li>
                      <li>Nhóm đồng bộ: {g.limits?.maxGroups ?? 'Không giới hạn'}</li>
                      <li>Bài đăng mỗi ngày: {g.limits?.maxPostsPerDay ?? 'Không giới hạn'}</li>
                    </ul>

                    <button
                      onClick={() => chayMua(() => mua(g))}
                      disabled={!g.available || dangMua || dangDung}
                      aria-busy={dangMua || undefined}
                      className={
                        'mt-4 w-full rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ' +
                        (dangDung
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400'
                          : 'bg-brand-600 text-white hover:bg-brand-700')
                      }
                    >
                      {dangDung
                        ? `Còn ${quyen?.daysLeft ?? '?'} ngày`
                        : !g.available
                        ? 'Chưa mở bán'
                        : dangMua
                        ? 'Đang tạo đơn...'
                        : quyen?.status === 'EXPIRED' && quyen.planId === g.id
                        ? 'Gia hạn'
                        : 'Chọn gói này'}
                    </button>
                  </div>
                );
              })}
            </div>

            {loiMua && (
              <p className="mt-3 rounded-xl bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-400">{loiMua}</p>
            )}

            <p className="mt-4 text-xs text-slate-400">
              Gói đang dùng vẫn chạy hết hạn hiện tại. Mua gói mới sẽ thay thế gói cũ khi được kích hoạt.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ===== MENU TÀI KHOẢN Ở GÓC TRÊN PHẢI =====
 *
 * VÌ SAO CÓ
 *
 * Trước 21/09/2026 chỗ này là một `<div>` trơ: hiện hai chữ viết tắt, có
 * `title`, và KHÔNG làm gì khi bấm. Người dùng quen với việc avatar góc phải là
 * menu tài khoản nên bấm vào — không ra gì. Đây đúng kiểu lỗi đã sửa ở nút
 * "AI Spin": giao diện trông dùng được nhưng câm.
 *
 * Hậu quả cụ thể: lối đăng xuất duy nhất nằm tận đáy thanh bên trái, xa chỗ
 * người dùng tìm. Trên máy chung, không tìm thấy nút đăng xuất là rủi ro thật.
 *
 * VÌ SAO KHÔNG HIỆN ẢNH Ở ĐÂY
 *
 * Khối này là tài khoản FBMax (`phien.user`), KHÔNG phải tài khoản Facebook.
 * Nó không có avatar, và cũng không được mượn ảnh của actor Facebook bên cạnh —
 * hai danh tính khác nhau, gộp lại là nói dối người dùng đang đăng nhập bằng gì.
 * Chữ viết tắt ở đây là giá trị đúng, không phải fallback khi lỗi.
 *
 * ĐĂNG XUẤT PHẢI KHOÁ THEO UI-01
 *
 * `dangXuat` chuyển trang bằng `location.href`. Bấm hai lần nhanh sẽ gửi hai
 * lời gọi và hiện hai thông báo lỗi chồng nhau. Dùng `useKhoaGui` giống hệt
 * `ThanhBen.tsx` — cùng một ý định ghi thì cùng một cách khoá.
 */
function MenuTaiKhoan({
  phien, onDangXuat, onNangCap,
}: { phien: Phien | null; onDangXuat: () => Promise<void>; onNangCap: () => void }) {
  const [mo, setMo] = useState(false);
  const [dangDangXuat, chayDangXuat] = useKhoaGui();
  const boc = useRef<HTMLDivElement>(null);

  // Đóng khi bấm ra ngoài hoặc nhấn Esc. Thiếu hai lối này thì menu kẹt lại,
  // che mất nội dung phía dưới mà người dùng không biết cách tắt.
  useEffect(() => {
    if (!mo) return;
    const ngoai = (e: MouseEvent) => {
      if (boc.current && !boc.current.contains(e.target as Node)) setMo(false);
    };
    const phim = (e: KeyboardEvent) => { if (e.key === 'Escape') setMo(false); };
    document.addEventListener('mousedown', ngoai);
    document.addEventListener('keydown', phim);
    return () => {
      document.removeEventListener('mousedown', ngoai);
      document.removeEventListener('keydown', phim);
    };
  }, [mo]);

  const nguoiDung = phien?.user;
  const quyen = phien?.entitlement;
  const vietTat = (nguoiDung?.name || 'FB').slice(0, 2).toUpperCase();

  return (
    <div className="relative shrink-0" ref={boc}>
      <button
        type="button"
        onClick={() => setMo(v => !v)}
        aria-haspopup="menu"
        aria-expanded={mo}
        aria-label={nguoiDung?.email ? `Tài khoản ${nguoiDung.email}` : 'Tài khoản FBMax'}
        title={nguoiDung?.email || 'Tài khoản FBMax'}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-bold text-brand-700 transition hover:ring-2 hover:ring-brand-200 focus:outline-none focus:ring-2 focus:ring-brand-500/40 dark:bg-brand-500/10 dark:text-brand-300 dark:hover:ring-brand-500/30"
      >
        {vietTat}
      </button>

      {mo && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800"
        >
          <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-700">
            <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
              {nguoiDung?.name || 'Tài khoản FBMax'}
            </p>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">
              {nguoiDung?.email || 'Chưa đăng nhập'}
            </p>
            {/* Nói đúng trạng thái gói. `—` khi chưa rõ, không đoán là "chưa có". */}
            <p className="mt-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
              {!quyen
                ? '—'
                : quyen.active
                ? `${quyen.planName || 'Gói đang dùng'} · còn ${quyen.daysLeft ?? '?'} ngày`
                : quyen.status === 'EXPIRED'
                ? `${quyen.planName || 'Gói'} đã hết hạn`
                : 'Chưa có gói'}
            </p>
          </div>

          {/* Mở popup NGAY TRONG APP, không rời sang trang tiếp thị. Người dùng
              đang làm dở một việc; đẩy họ ra ngoài là bắt tự tìm đường quay lại.
              Popup còn biết gói nào đang dùng, trang tĩnh thì không. */}
          <button
            role="menuitem"
            type="button"
            onClick={() => { setMo(false); onNangCap(); }}
            className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/50"
          >
            <Tag className="h-4 w-4 shrink-0" />
            Xem bảng giá
          </button>

          {/* Lối nâng cấp nổi bật, CHỈ khi gói không còn hiệu lực. Mời người đang
              dùng gói còn hạn là làm phiền, và dễ khiến họ tưởng gói đã hỏng.
              (Họ vẫn xem được bảng giá qua lối ngay trên.) */}
          {quyen && !quyen.active && (
            <button
              role="menuitem"
              type="button"
              onClick={() => { setMo(false); onNangCap(); }}
              className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-500/10"
            >
              <Sparkles className="h-4 w-4 shrink-0" />
              {quyen.status === 'EXPIRED' ? 'Gia hạn gói' : 'Kích hoạt gói'}
            </button>
          )}

          {nguoiDung && (
            <button
              role="menuitem"
              type="button"
              onClick={() => chayDangXuat(onDangXuat)}
              disabled={dangDangXuat}
              aria-busy={dangDangXuat || undefined}
              className="flex w-full items-center gap-2.5 border-t border-slate-100 px-4 py-2.5 text-sm font-semibold text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:text-rose-400 dark:hover:bg-rose-500/10"
            >
              {dangDangXuat
                ? <span aria-hidden="true" className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" />
                : <LogOut className="h-4 w-4 shrink-0" />}
              {dangDangXuat ? 'Đang đăng xuất...' : 'Đăng xuất'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ChuaDung({ nhan }: { nhan: string }) {
  return (
    <div className="mx-auto max-w-[1440px] animate-fade-in">
      <div className="rounded-xl border border-slate-200/80 bg-white p-6 shadow-card dark:border-slate-700/60 dark:bg-slate-800">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Màn <span className="font-semibold text-slate-700 dark:text-slate-200">{nhan}</span> đang được dựng lại.
        </p>
      </div>
    </div>
  );
}
